/**
 * Tier of a pulled card relative to pack price (V/P bands).
 * Stored on `pack_opening_results.rarity` (legacy column name).
 * Eight tiers: five mid bands + `mythic` + `grail` (Curve L, see docs/PACK-DESIGN §1),
 * plus `god`, the God Pack: one roll that awards five cards, one each from rare, epic,
 * legendary, mythic and grail. Each of those cards keeps its own band tier; `god` is
 * the opening's tier and is never a single card's tier.
 */
export type OutcomeTier =
  | "common"
  | "uncommon"
  | "rare"
  | "epic"
  | "legendary"
  | "mythic"
  | "grail"
  | "god";

/** A tier a single card's value can resolve to: every tier except `god`. */
export type BandTier = Exclude<OutcomeTier, "god">;

/**
 * Display alias preserved for callers that previously folded `mythic` into `grail`.
 * `mythic` is now a real tier — `normalizePullTier` is identity at runtime so legacy
 * payload shapes keep type-checking without silently collapsing the new chase split.
 */
export type PullDisplayRarity = OutcomeTier;

export function normalizePullTier(r: PullDisplayRarity): OutcomeTier {
  return r;
}

/** Buckets for collection / analytics when catalog rarity is retired (USD cents). */
export type CollectionValueBand =
  | "unknown"
  | "under_50"
  | "50_99"
  | "100_249"
  | "250_499"
  | "500_4999"
  | "5k_plus";

export function collectionValueBandFromCents(cents: number | null | undefined): CollectionValueBand {
  if (cents == null || !Number.isFinite(cents) || cents < 0) return "unknown";
  if (cents < 5_000) return "under_50";
  if (cents < 10_000) return "50_99";
  if (cents < 25_000) return "100_249";
  if (cents < 50_000) return "250_499";
  if (cents < 500_000) return "500_4999";
  return "5k_plus";
}

export interface Card {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  sport: string;
  year: number;
  playerName: string;
  setName: string;
  cardNumber: string;
  estimatedValue?: number;
  /** Card Ladder valuation exposed as a display-only USD amount. */
  marketValue?: number;
  /** Resolved retail source (`card_ladder_estimate` | `manual_override`). */
  priceSource?: string;
  createdAt: Date;
}

/**
 * How the current owner came to hold a card: their latest acquisition in the
 * ownership ledger. `marketplace` = bought from another collector, `house` =
 * bought from an MOT listing (no seller), `pack` = pulled from a pack,
 * `gift` = transferred to the owner by MOT as an owner-directed gift.
 */
export interface OwnedCardAcquisition {
  source: "pack" | "marketplace" | "house" | "gift";
  /** What the buyer paid for a marketplace/house purchase (no buyer fee exists); null for a pull. */
  priceCents: number | null;
  /** ISO 8601 time of that acquisition. */
  acquiredAt: string;
}

export interface OwnedCard {
  id: string;
  /** Catalog design id — `null` when identified by vendor only (not yet linked to `cards`). */
  cardId: string | null;
  userId: string;
  packOpenId?: string;
  acquiredAt: Date;
  serialNumber: string;
  condition: CardCondition;
  isListed: boolean;
  /** When set, the user can keep or sell back this pull on /collection until `deadline`. */
  pendingDecision?: {
    /** Pack opening id (same as `eventId` in POST `/api/v1/packs/events/:eventId/decide`). Empty for `sellbackPath: "marketplace"`. */
    packOpeningId: string;
    sellbackOfferCents: number | null;
    /** ISO 8601 timestamp; empty if deadline not set on row. */
    deadline: string;
    /** True if the buyer already chose Keep; sell-back remains available until `deadline`. */
    graceAfterKeep?: boolean;
    /** `"initial"` = the pull's original keep/sell-back window; `"daily"` = the recurring window after it. */
    windowKind?: "initial" | "daily";
    /** For `windowKind: "daily"` only: whether today's window is currently open. */
    windowOpen?: boolean;
    /** For `windowKind: "daily"` when closed: ISO 8601 timestamp of the next window's open time. */
    nextWindowOpensAt?: string;
    /** For `windowKind: "daily"` when open with an offer: the quote backing `sellbackOfferCents`. */
    quoteId?: string;
    /**
     * Which sell-back endpoint applies. `"marketplace"` = a card the owner bought
     * on the marketplace: POST `/api/v1/marketplace/sellback/:inventoryItemId`
     * with `expectedCreditCents`. `"admin_gift"` = a card the owner was gifted by MOT:
     * POST `/api/v1/ownership/:inventoryItemId/gift-sellback` with `expectedCreditCents`.
     * Absent = the pack decide endpoint.
     */
    sellbackPath?: "pack" | "marketplace" | "admin_gift";
    /** For `sellbackPath: "marketplace"` or `"admin_gift"` with an offer: the exact wallet credit after the selling fee. */
    expectedCreditCents?: number;
    /**
     * Pack path, rip partly paid with Max Bucks: the Max Bucks spent and the rip's
     * price, so the offer can show the MB/cash split the sell-back credits.
     */
    paidMaxBucksCents?: number;
    paidPriceCents?: number;
    /** Seeded cents that paid for the rip (caps the MB share like the server). */
    paidSeededCents?: number;
  };
  /** How this card was originally acquired (mirrors the first OwnershipEvent). */
  source?: import("./ownership").OwnershipEventType;
}

export type CardCondition = "mint" | "near_mint" | "excellent" | "good" | "fair" | "poor";
